import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, GitBranch } from "lucide-react";
import { LoopPair } from "@/components/adaptive-loop/RegulationLoops";
import { productUrl } from "@/config/siteUrls";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 w-[800px] h-[800px] forr-radial-teal opacity-70 pointer-events-none" />
      <div className="absolute top-[420px] right-[-200px] w-[700px] h-[700px] forr-radial-warm opacity-35 pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <div className="inline-flex items-center gap-3 mb-10">
              <span className="forr-hairline" />
              <span data-testid="hero-overline" className="forr-overline">
                Adaptive Physiology™ — A methodology by FORR Health
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display forr-text-ink text-[44px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[80px] font-medium tracking-[-0.03em]"
            >
              Your nervous system is doing{" "}
              <span className="font-editorial forr-text-teal-deep">exactly</span>{" "}
              what it was trained to do.
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-10 max-w-3xl text-base sm:text-lg lg:text-xl font-light forr-text-soft leading-relaxed"
            >
              Adaptive Physiology maps the route from nervous-system activation
              to behaviour, then uses matched physiological inputs to disrupt
              dependency reinforcement and support recovery.
            </p>

            <p
              data-testid="hero-supporting"
              className="mt-6 max-w-2xl text-sm sm:text-base font-light forr-text-mute leading-relaxed"
            >
              The goal is not to eliminate stress or emotion. It is to change
              where the activated system goes next.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href="#two-loops"
                data-testid="hero-cta-primary-link"
                className="forr-btn-primary group inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-semibold tracking-wide"
              >
                Explore the two loops
                <ArrowRight
                  size={16}
                  strokeWidth={2}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
              <a
                href={productUrl("/assessment/start")}
                data-testid="hero-cta-secondary"
                className="forr-btn-secondary inline-flex px-6 py-4 rounded-full text-sm font-medium tracking-wide"
              >
                Begin the assessment
              </a>
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.15,
            }}
            className="col-span-12 lg:col-span-4"
          >
            <div
              className="rounded-[24px] p-7 forr-surface"
              style={{
                border: "1px solid var(--forr-line)",
                boxShadow: "var(--forr-shadow-md)",
              }}
            >
              <GitBranch
                size={20}
                strokeWidth={1.5}
                className="forr-text-teal-deep"
              />
              <h2 className="mt-5 font-display forr-text-ink text-2xl font-medium tracking-[-0.02em]">
                Same activation. Two possible routes.
              </h2>
              <p className="mt-4 text-sm font-light forr-text-soft leading-relaxed">
                One route reinforces outsourced relief and dependency. The other
                introduces an adaptive intervention and completes the
                activation–recovery cycle.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#111315] p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#929A9F]">
                    Centre
                  </div>
                  <div className="mt-2 font-display text-sm text-[#F4F1EA]">
                    Dependency
                  </div>
                </div>
                <div
                  className="rounded-xl p-4"
                  style={{
                    backgroundColor: "var(--forr-teal-tint)",
                    border: "1px solid var(--forr-teal-soft)",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] forr-text-mute">
                    Centre
                  </div>
                  <div className="mt-2 font-display text-sm forr-text-teal-deep">
                    Homeostasis
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1,
            ease: [0.16, 1, 0.3, 1],
            delay: 0.28,
          }}
          className="mt-20"
        >
          <LoopPair size="hero" />
        </motion.div>

        <motion.a
          href="#two-loops"
          data-testid="hero-scroll-cue"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-12 hidden md:flex items-center justify-center gap-2 forr-text-mute hover:forr-text-teal-deep transition-colors"
        >
          <span className="text-[11px] tracking-[0.28em] uppercase">
            Understand the pathways
          </span>
          <ChevronDown size={16} strokeWidth={1.5} className="animate-bounce" />
        </motion.a>
      </div>
    </section>
  );
};

export default Hero;
